import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase';

const COLLECTION_NAME = 'pageContent';

/**
 * Get content for a specific page and section
 * @param {string} pageId - Page identifier (e.g., 'home', 'about', 'contact')
 * @param {string} sectionId - Section identifier (e.g., 'hero', 'about-intro', 'contact-info')
 * @returns {Promise<Object|null>} Content object or null if not found
 */
export const getPageContent = async (pageId, sectionId) => {
  try {
    if (!db) {
      console.warn('Firestore not initialized');
      return null;
    }

    const docRef = doc(db, COLLECTION_NAME, `${pageId}_${sectionId}`);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data()
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error getting page content:', error);
    return null;
  }
};

/**
 * Get all content for a specific page
 * @param {string} pageId - Page identifier
 * @returns {Promise<Object>} Object with section IDs as keys and content as values
 */
export const getAllPageContent = async (pageId) => {
  try {
    if (!db) {
      console.warn('Firestore not initialized');
      return {};
    }

    const collectionRef = collection(db, COLLECTION_NAME);
    const snapshot = await getDocs(collectionRef);
    
    const content = {};
    snapshot.forEach((doc) => {
      const data = doc.data();
      if (data.pageId === pageId) {
        content[data.sectionId] = {
          id: doc.id,
          ...data
        };
      }
    });
    
    return content;
  } catch (error) {
    console.error('Error getting all page content:', error);
    return {};
  }
};

/**
 * Save or update page content
 * @param {string} pageId - Page identifier
 * @param {string} sectionId - Section identifier
 * @param {Object} contentData - Content data to save
 * @returns {Promise<boolean>} Success status
 */
export const savePageContent = async (pageId, sectionId, contentData) => {
  try {
    if (!db) {
      throw new Error('Firestore not initialized');
    }

    const docId = `${pageId}_${sectionId}`;
    const docRef = doc(db, COLLECTION_NAME, docId);
    
    await setDoc(docRef, {
      pageId,
      sectionId,
      ...contentData,
      updatedAt: serverTimestamp(),
    }, { merge: true });
    
    return true;
  } catch (error) {
    console.error('Error saving page content:', error);
    throw error;
  }
};

/**
 * Get all pages with their sections (for admin dashboard)
 * @returns {Promise<Array>} Array of page objects with sections
 */
export const getAllPages = async () => {
  try {
    if (!db) {
      console.warn('Firestore not initialized');
      return [];
    }

    const collectionRef = collection(db, COLLECTION_NAME);
    const snapshot = await getDocs(collectionRef);
    
    const pagesMap = {};
    snapshot.forEach((doc) => {
      const data = doc.data();
      const { pageId, sectionId } = data;
      
      if (!pagesMap[pageId]) {
        pagesMap[pageId] = {
          pageId,
          sections: []
        };
      }
      
      pagesMap[pageId].sections.push({
        id: doc.id,
        sectionId,
        ...data
      });
    });
    
    return Object.values(pagesMap);
  } catch (error) {
    console.error('Error getting all pages:', error);
    return [];
  }
};

/**
 * Delete page content
 * @param {string} pageId - Page identifier
 * @param {string} sectionId - Section identifier
 * @returns {Promise<boolean>} Success status
 */
export const deletePageContent = async (pageId, sectionId) => {
  try {
    if (!db) {
      throw new Error('Firestore not initialized');
    }

    const docId = `${pageId}_${sectionId}`;
    const docRef = doc(db, COLLECTION_NAME, docId);
    
    await updateDoc(docRef, {
      deleted: true,
      deletedAt: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    console.error('Error deleting page content:', error);
    throw error;
  }
};


